/**
 * Bridges the Usercentrics Web CMP (loaded in public/index.html) into React state.
 *
 * Why this exists:
 *   The CMP runs outside React (loader script → window.__ucCmp). Downstream code
 *   (useServiceConsent, TrackingInitializer, YouTube gate) needs a stable
 *   { isInitialized, consents } snapshot inside the tree.
 *
 * Data flow:
 *   CMP events / readConsentMap() → consents state → useServiceConsent → TrackingInitializer
 *
 * CMP API reference: https://usercentrics.com/docs/web/features/api/control-functionality/
 *
 * Mounted in Providers.tsx above TrackingInitializer so consent is known before trackers react.
 */
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { USERCENTRICS_ENABLE } from 'config/env';
import { updateGoogleConsentForFirebase } from 'config/firebaseConfig';
import { capturePromiseRejection } from 'utils/capturePromiseRejection';
import log from 'utils/logger';
import { getUcCmp, hasUsercentricsLoader, readConsentMap } from './usercentricsApi';
import { UcCmpEventType, type UcCmpEvent } from './usercentricsTypes';
import { UC_SERVICES } from './usercentricsConfig';
import { UsercentricsContext, type UsercentricsContextValue } from './UsercentricsContext';

/** If CMP never exposes __ucCmp, stop waiting and leave gated trackers off. */
const CMP_INIT_TIMEOUT_MS = 10_000;

const isConsentChangeEvent = (event: UcCmpEvent): boolean => {
  const { type } = event.detail ?? {};
  return (
    type === UcCmpEventType.SAVE ||
    type === UcCmpEventType.ACCEPT_ALL ||
    type === UcCmpEventType.DENY_ALL
  );
};

type Props = {
  children: ReactNode;
};

export const UsercentricsProvider = ({ children }: Props): ReactElement => {
  // CMP is "on" only when env flag is set AND the loader script tag exists in index.html.
  const isCmpEnabled = USERCENTRICS_ENABLE && hasUsercentricsLoader();

  // When CMP is off, we start initialized so useServiceConsent returns true immediately.
  const [isInitialized, setIsInitialized] = useState(!isCmpEnabled);
  const [cmpLoadFailed, setCmpLoadFailed] = useState(false);
  // templateId → granted (true/false). Drives useServiceConsent / TrackingInitializer.
  const [consents, setConsents] = useState<Record<string, boolean>>({});

  // Avoids firing the 10s timeout warning after CMP has already become available.
  const cmpReadyRef = useRef(!isCmpEnabled);

  const refreshConsents = useCallback(async (): Promise<void> => {
    if (!isCmpEnabled) {
      setIsInitialized(true);
      return;
    }

    if (getUcCmp()) {
      cmpReadyRef.current = true;
      setCmpLoadFailed(false);
    }

    const nextConsents = await readConsentMap();
    setConsents(nextConsents);
    setIsInitialized(true);

    if (nextConsents[UC_SERVICES.firebase] === true) {
      updateGoogleConsentForFirebase(true);
    } else if (nextConsents[UC_SERVICES.firebase] === false) {
      updateGoogleConsentForFirebase(false);
    }
  }, [isCmpEnabled]);

  useEffect(() => {
    if (!isCmpEnabled) return undefined;

    let isMounted = true;

    const handleInitialized = (): void => {
      if (!isMounted) return;
      cmpReadyRef.current = true;
      setCmpLoadFailed(false);
      capturePromiseRejection(refreshConsents(), 'Failed to refresh Usercentrics consents');
    };

    const handleCmpEvent = (event: Event): void => {
      if (!isConsentChangeEvent(event as UcCmpEvent)) return;
      capturePromiseRejection(refreshConsents(), 'Failed to refresh Usercentrics consents');
    };

    window.addEventListener('UC_UI_INITIALIZED', handleInitialized);
    window.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    if (getUcCmp()) {
      capturePromiseRejection(refreshConsents(), 'Failed to refresh Usercentrics consents');
    }

    const timeoutId = window.setTimeout(() => {
      if (!isMounted || cmpReadyRef.current) return;

      if (!getUcCmp()) {
        setCmpLoadFailed(true);
        log.warn(
          '[Usercentrics] CMP did not initialize within 10s — consent-gated trackers will stay off. ' +
            'Check domain whitelist, ad blockers, and the CMP loader in public/index.html.'
        );
      }

      capturePromiseRejection(refreshConsents(), 'Failed to refresh Usercentrics consents');
    }, CMP_INIT_TIMEOUT_MS);

    return () => {
      isMounted = false;
      window.removeEventListener('UC_UI_INITIALIZED', handleInitialized);
      window.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
      window.clearTimeout(timeoutId);
    };
  }, [isCmpEnabled, refreshConsents]);

  const contextValue = useMemo<UsercentricsContextValue>(
    () => ({
      isCmpEnabled,
      isInitialized,
      cmpLoadFailed,
      consents,
      refreshConsents,
    }),
    [cmpLoadFailed, consents, isCmpEnabled, isInitialized, refreshConsents]
  );

  return (
    <UsercentricsContext.Provider value={contextValue}>{children}</UsercentricsContext.Provider>
  );
};
